import { useEffect, useRef, useState } from "react";
import type {
  ShiftFormActor,
  ShiftProductionForm,
  ShiftProductionFormPayload,
  ShiftProductionFormListItem,
} from "../../../shared/shift-production-form/contracts";
import { AuthScreen } from "../auth/AuthScreen";
import { SystemState } from "../../shared/components/SystemState";
import { ManagerFormWorkspace } from "./ManagerFormWorkspace";
import { OperatorPinDialog } from "./OperatorPinDialog";
import {
  beginPinAction,
  cancelPin,
  changePin,
  rejectPin,
  resolveShiftFormView,
  operatorAccessState,
} from "./pin-gate-state";
import { OperatorRecentFormsDialog } from "./OperatorRecentFormsDialog";
import { ShiftFormEditor } from "./ShiftFormEditor";
import {
  useOperatorFormWorkspace,
  isOperatorSessionError,
} from "./useOperatorFormWorkspace";
import { useLineMeasurementUnits } from "./useLineMeasurementUnits";
import { useDocumentSettings } from "./useDocumentSettings";
import { useTabletPortraitGuard } from "./useTabletPortraitGuard";
import { TabletRotateMessage } from "./TabletRotateMessage";
import { useShiftProductionFormWorkspace } from "./useShiftProductionFormWorkspace";
import "./shift-production-form.css";

export function ShiftProductionFormModule({
  initialFormId = null,
}: {
  initialFormId?: string | null;
}) {
  const sessionWorkspace = useShiftProductionFormWorkspace();
  const managerRequested =
    initialFormId !== null ||
    new URLSearchParams(window.location.search).get("manager") === "1";
  const actor =
    sessionWorkspace.session.phase === "ready"
      ? sessionWorkspace.session.actor
      : null;
  const view = resolveShiftFormView(managerRequested, actor?.kind ?? null);
  const managerLogoutStarted = useRef(false);

  useEffect(() => {
    if (!managerRequested || actor?.kind !== "OPERATOR") {
      managerLogoutStarted.current = false;
      return;
    }
    if (managerLogoutStarted.current) return;
    managerLogoutStarted.current = true;
    void sessionWorkspace.clearOperatorSession().catch(() => undefined);
  }, [actor?.kind, managerRequested, sessionWorkspace.clearOperatorSession]);

  if (managerRequested) {
    if (
      sessionWorkspace.session.phase === "loading" ||
      actor?.kind === "OPERATOR"
    )
      return (
        <SystemState
          mode="page"
          tone="loading"
          title="Yönetici erişimi hazırlanıyor"
          description="Kurumsal oturum doğrulanıyor."
        />
      );
    if (sessionWorkspace.session.phase === "error")
      return (
        <AuthScreen
          mode="service-unavailable"
          onRetry={sessionWorkspace.reload}
        />
      );
    if (view === "AUTH") return <AuthScreen />;
    return (
      <main className="shift-form-shell">
        <nav className="shift-manager-access-bar" aria-label="Form görünümü">
          <a href="/vardiya-uretim-formu">Boş üretim formuna dön</a>
        </nav>
        <ManagerFormWorkspace initialFormId={initialFormId} />
      </main>
    );
  }

  return (
    <main className="shift-form-shell">
      <OperatorWorkspace
        actor={actor?.kind === "OPERATOR" ? actor : null}
        sessionWorkspace={sessionWorkspace}
      />
    </main>
  );
}

type SessionWorkspace = ReturnType<typeof useShiftProductionFormWorkspace>;

function OperatorWorkspace({
  actor,
  sessionWorkspace,
}: {
  actor: ShiftFormActor | null;
  sessionWorkspace: SessionWorkspace;
}) {
  const workspace = useOperatorFormWorkspace();
  const [recentOpen, setRecentOpen] = useState(false);
  const lineUnits = useLineMeasurementUnits();
  const [verifiedOperatorId, setVerifiedOperatorId] = useState<string | null>(
    null,
  );
  const documentSettings = useDocumentSettings(verifiedOperatorId !== null);
  const portraitBlocked = useTabletPortraitGuard();
  const [pinGate, setPinGate] = useState(() =>
    beginPinAction({ kind: "OPEN" }),
  );
  const [pinBusy, setPinBusy] = useState(false);
  const pinInFlight = useRef(false);
  const authGeneration = useRef(0);
  const saveResolver = useRef<
    ((form: ShiftProductionForm | null) => void) | null
  >(null);
  const operatorId = actor?.kind === "OPERATOR" ? actor.id : null;
  const { entryRequired, privateLocked, gateOpen, maskPrivateForm } =
    operatorAccessState({
      verifiedOperatorId,
      sessionOperatorId: operatorId,
      formOwnerId: workspace.form?.ownerOperatorId ?? null,
      pendingAction: pinGate.action !== null,
    });

  useEffect(
    () => () => {
      authGeneration.current += 1;
      saveResolver.current?.(null);
    },
    [],
  );

  const closeGate = (result: ShiftProductionForm | null = null) => {
    saveResolver.current?.(result);
    saveResolver.current = null;
    setPinGate(cancelPin());
  };
  const requirePinForSave = (
    payload: ShiftProductionFormPayload,
    submit: boolean,
  ) =>
    new Promise<ShiftProductionForm | null>((resolve) => {
      saveResolver.current?.(null);
      saveResolver.current = resolve;
      setPinGate(beginPinAction({ kind: submit ? "SUBMIT" : "SAVE", payload }));
    });
  const requestSave = async (
    payload: ShiftProductionFormPayload,
    submit = false,
  ) => {
    if (entryRequired || !operatorId) return requirePinForSave(payload, submit);
    try {
      return await workspace.save(payload, submit, operatorId);
    } catch (reason) {
      if (!isOperatorSessionError(reason)) throw reason;
      await sessionWorkspace.clearOperatorSession().catch(() => undefined);
      return requirePinForSave(payload, submit);
    }
  };
  const authenticateAndContinue = async () => {
    if (pinGate.pin.length !== 4 || pinInFlight.current) return;
    pinInFlight.current = true;
    setPinBusy(true);
    const generation = ++authGeneration.current;
    try {
      const response = await sessionWorkspace.verifyOperatorPin(pinGate.pin);
      if (generation !== authGeneration.current) return;
      const authenticatedActor = response.actor;
      if (!authenticatedActor || authenticatedActor.kind !== "OPERATOR")
        throw new Error("Operatör kimliği doğrulanamadı.");
      const expectedOwner =
        workspace.form?.ownerOperatorId ?? verifiedOperatorId;
      if (expectedOwner && expectedOwner !== authenticatedActor.id) {
        await sessionWorkspace.clearOperatorSession().catch(() => undefined);
        if (generation === authGeneration.current)
          setPinGate((current) =>
            rejectPin(
              current,
              "Ekrandaki form başka bir operatöre ait. Aynı operatörün PIN’ini girin veya formu kapatarak operatörü değiştirin.",
            ),
          );
        return;
      }
      if (verifiedOperatorId === null)
        await workspace.resume(authenticatedActor.id);
      if (generation !== authGeneration.current) return;
      sessionWorkspace.acceptSession(response);
      setVerifiedOperatorId(authenticatedActor.id);
      const action = pinGate.action;
      if (action?.kind === "SAVE" || action?.kind === "SUBMIT") {
        const result = await workspace.save(
          action.payload,
          action.kind === "SUBMIT",
          authenticatedActor.id,
        );
        if (generation === authGeneration.current) closeGate(result);
      } else closeGate();
    } catch (reason) {
      if (generation === authGeneration.current)
        setPinGate((current) =>
          rejectPin(
            current,
            reason instanceof Error
              ? reason.message
              : "Form açılamadı. Yeniden deneyin.",
          ),
        );
    } finally {
      pinInFlight.current = false;
      if (generation === authGeneration.current) setPinBusy(false);
    }
  };
  const handleRecentError = async (reason: unknown) => {
    if (isOperatorSessionError(reason)) {
      setRecentOpen(false);
      await sessionWorkspace.clearOperatorSession().catch(() => undefined);
    }
  };
  const showRecent = (offset = 0) => {
    setRecentOpen(true);
    void workspace.loadRecent(offset).catch(handleRecentError);
  };
  const editRecent = async (form: ShiftProductionFormListItem) => {
    if (!operatorId || entryRequired) return;
    try {
      const result = await workspace.openSubmitted(
        form.id,
        form.version,
        operatorId,
      );
      if (result) setRecentOpen(false);
    } catch (reason) {
      await handleRecentError(reason);
    }
  };
  const changeOperator = () => {
    setRecentOpen(false);
    authGeneration.current += 1;
    closeGate();
    workspace.startNew();
    setVerifiedOperatorId(null);
    setPinGate(beginPinAction({ kind: "OPEN" }));
    setPinBusy(true);
    void sessionWorkspace
      .clearOperatorSession()
      .catch(() => undefined)
      .finally(() => setPinBusy(false));
  };
  return (
    <>
      <div
        hidden={portraitBlocked}
        aria-hidden={portraitBlocked || undefined}
        inert={portraitBlocked || undefined}
      >
      <div
        className={maskPrivateForm ? "shift-form-private-lock" : undefined}
        aria-hidden={maskPrivateForm || undefined}
        inert={maskPrivateForm || undefined}
      >
        {verifiedOperatorId === null ? (
          <div className="shift-operator-entry-canvas" aria-hidden="true" />
        ) : documentSettings.loading ? (
          <SystemState
            mode="page"
            tone="loading"
            title="Doküman bilgileri yükleniyor"
            description="Form açılmadan önce güncel doküman bilgileri alınıyor."
          />
        ) : documentSettings.error || !documentSettings.settings ? (
          <SystemState
            mode="page"
            tone="danger"
            title="Doküman bilgileri alınamadı"
            description={documentSettings.error ?? "Form açılmadan önce doküman bilgileri gerekli."}
            primaryAction={{
              label: "Yeniden dene",
              onClick: () => void documentSettings.reload(),
            }}
          />
        ) : (
          <ShiftFormEditor
            actorName={actor?.kind === "OPERATOR" ? actor.name : ""}
            interactionDisabled={portraitBlocked}
            documentSettings={documentSettings.settings}
            lineUnits={lineUnits.items}
            lineUnitsError={lineUnits.error}
            onRetryLineUnits={lineUnits.retry}
            form={workspace.form}
            selectionVersion={workspace.selectionVersion}
            busy={workspace.busy || pinBusy || gateOpen || recentOpen}
            error={workspace.error}
            notice={workspace.notice}
            onSave={requestSave}
            onRecentForms={() => showRecent()}
            onNew={() => {
              void documentSettings.reload();
              workspace.startNew();
            }}
          />
        )}
      </div>
      <OperatorRecentFormsDialog
        open={recentOpen && !gateOpen && !portraitBlocked}
        items={workspace.recentForms}
        busy={workspace.recentBusy}
        error={workspace.recentError}
        onClose={() => setRecentOpen(false)}
        onRetry={() => showRecent(workspace.recentOffset)}
        hasPrevious={workspace.recentOffset > 0}
        hasNext={
          workspace.recentOffset + workspace.recentForms.length <
          workspace.recentTotal
        }
        onPrevious={() => showRecent(Math.max(0, workspace.recentOffset - 50))}
        onNext={() => showRecent(workspace.recentOffset + 50)}
        onEdit={(form) => void editRecent(form)}
      />
      <OperatorPinDialog
        open={gateOpen && !portraitBlocked}
        action={pinGate.action ?? { kind: "OPEN" }}
        pin={pinGate.pin}
        error={pinGate.error}
        busy={pinBusy}
        privateLocked={privateLocked}
        entryRequired={entryRequired}
        onPinChange={(value) =>
          setPinGate((current) => changePin(current, value))
        }
        onSubmit={() => void authenticateAndContinue()}
        onCancel={() => closeGate()}
        onDiscardPrivateForm={changeOperator}
      />
      </div>
      {portraitBlocked ? <TabletRotateMessage /> : null}
    </>
  );
}
